import { db } from '@app/icare/client/db'
import { localPaginatedQueryOptions } from '@app/icare/client/db/client/localQueryOptions'
import { FindingsAlert, FindingsSelector } from '@app/icare/components/findings'
import { FormStatusBadge } from '@app/icare/components/status/FormStatusBadge'
import { queryKit } from '@app/icare/db/query/queryKit'
import type { PatientFormCommon } from '@app/icare/db/schema/form/PatientFormCommon'
import type { PatientSignedFormTypeType } from '@app/icare/db/schema/form/PatientFormType'
import { selectByFindingIds } from '@app/icare/entities/patient/queries/patientFindingsQueryOptions'
import { PatientId } from '@app/icare/entities/patient/schema/PatientId'
import { SignFormStatusEnum, type SignFormStatusType } from '@app/icare/entities/patientForm/schema/PatientForm'
import { Trans } from '@lingui/react/macro'
import { entityParams } from '@pkg/db/utils'
import { formatDateTime } from '@pkg/shared/utils'
import { ActionColumnButton } from '@pkg/ui/button'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Flex, Table } from 'antd'
import type { ColumnType } from 'antd/es/table'
import type React from 'react'
import { useMemo } from 'react'

export const Route = createFileRoute('/_app/patienten/$id/_edit/_documents/unterschriftsdokumente/')({
  component: RouteComponent,
  params: entityParams({ id: PatientId }),
})

interface OverviewRow {
  type: PatientSignedFormTypeType
  label: React.ReactNode
  to: string
  lastDocumentAt: string | null | undefined
  lastDocumentStatus: string | null | undefined
}

const SIGNED_TYPES: Omit<OverviewRow, 'lastDocumentAt' | 'lastDocumentStatus'>[] = [
  {
    type: 'PatientInformation',
    label: <Trans>Patienteninformationen</Trans>,
    to: '/patienten/$id/unterschriftsdokumente/patienteninformationen',
  },
  {
    type: 'PatientConsentAndConfidentiality',
    label: <Trans>Einwilligung & Schweigepflicht</Trans>,
    to: '/patienten/$id/unterschriftsdokumente/einwilligung-schweigepflicht',
  },
  {
    type: 'PatientSupply',
    label: <Trans>Versorgungsauftrag</Trans>,
    to: '/patienten/$id/unterschriftsdokumente/versorgungsauftrag',
  },
]

function RouteComponent() {
  const { id: patientId } = Route.useParams()

  const { data, isFetching } = useQuery(
    localPaginatedQueryOptions({
      query: db
        .selectFrom('patientForm')
        .selectAll()
        .$call(queryKit.deleted('exclude'))
        .where('patientId', '=', patientId)
        .where('type', 'in', ['PatientInformation', 'PatientConsentAndConfidentiality', 'PatientSupply']),
      params: queryKit.parseQuery({}),
    }),
  )

  const rows: OverviewRow[] = useMemo(() => {
    const forms = (data?.data ?? []) as PatientFormCommon[]
    return SIGNED_TYPES.map(entry => {
      const latest = forms
        .filter(f => f.type === entry.type)
        .sort((a, b) => (b.createdAt?.toString() ?? '').localeCompare(a.createdAt?.toString() ?? ''))[0]
      return {
        ...entry,
        lastDocumentAt: latest?.createdAt as string | null | undefined,
        lastDocumentStatus: latest?.status,
      }
    })
  }, [data])

  const columns: ColumnType<OverviewRow>[] = [
    {
      key: 'type',
      title: <Trans>Dokument</Trans>,
      render: (_: unknown, record: OverviewRow) => record.label,
    },
    {
      key: 'lastDocumentAt',
      title: <Trans>Letztes Dokument</Trans>,
      render: (_: unknown, record: OverviewRow) => formatDateTime(record.lastDocumentAt) ?? '—',
    },
    {
      key: 'status',
      title: <Trans>Status</Trans>,
      render: (_: unknown, record: OverviewRow) =>
        record.lastDocumentStatus ? (
          <FormStatusBadge value={record.lastDocumentStatus as SignFormStatusType} statusEnum={SignFormStatusEnum} />
        ) : (
          '—'
        ),
    },
    {
      key: 'actions',
      title: <Trans>Aktionen</Trans>,
      render: (_: unknown, record: OverviewRow) => (
        <ActionColumnButton to={record.to} params={{ id: patientId }} type="showEntity" />
      ),
    },
  ]

  return (
    <Flex vertical gap="small">
      <FindingsSelector
        selector={selectByFindingIds([
          'FormsPatientInformationRequired',
          'FormsPatientConsentAndConfidentialityRequired',
          'FormsPatientSupplyRequired',
        ])}
      >
        {f => <FindingsAlert findings={f} />}
      </FindingsSelector>
      <Table<OverviewRow>
        dataSource={rows}
        rowKey="type"
        columns={columns}
        size="small"
        pagination={false}
        loading={isFetching}
      />
    </Flex>
  )
}
